import React, { FC, Fragment } from "react";
import { Popover, Transition } from "@headlessui/react";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import ButtonThird from "shared/Button/ButtonThird";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import convertNumbThousand from "utils/convertNumbThousand";

interface PriceRangeFilterProps {
  priceRange: [number, number];
  onPriceChange: (value: number | number[]) => void;
  onClear: (close: () => void) => void;
  onApply: (close: () => void) => void;
}

const PriceRangeFilter: FC<PriceRangeFilterProps> = ({
  priceRange,
  onPriceChange,
  onClear,
  onApply,
}) => {
  const isFilterApplied = priceRange[0] !== 0 || priceRange[1] !== 5000;

  return (
    <Popover className="relative">
      {({ open, close }) => (
        <>
          <Popover.Button
            className={`flex items-center justify-center px-4 py-2 text-sm rounded-full border focus:outline-none transition-all ${
              isFilterApplied
                ? "bg-[#f5694b] text-white border-[#f5694b]"
                : "border-neutral-300 dark:border-neutral-700 text-neutral-700 dark:text-neutral-200 hover:border-neutral-400 dark:hover:border-neutral-500 bg-white dark:bg-neutral-800"
            }`}
          >
            Budget
            <i className="las la-angle-down ml-2"></i>
          </Popover.Button>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <Popover.Panel className="absolute z-10 w-screen max-w-sm px-4 mt-3 left-0 sm:px-0 sm:left-0 transform -translate-x-1/2 left-1/2 sm:translate-x-0 sm:left-0">
              <div className="overflow-hidden rounded-2xl shadow-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700">
                <div className="relative flex flex-col px-5 py-6 space-y-8">
                  <div className="space-y-5">
                    <span className="font-medium text-neutral-800 dark:text-neutral-200">
                      Price
                    </span>
                    <Slider
                      range
                      min={0}
                      max={5000}
                      step={1}
                      value={priceRange}
                      onChange={onPriceChange}
                      trackStyle={[{ backgroundColor: "#f5694b" }]}
                      handleStyle={[
                        { borderColor: "#f5694b", backgroundColor: "#fff" },
                        { borderColor: "#f5694b", backgroundColor: "#fff" },
                      ]}
                    />
                  </div>

                  <div className="flex justify-between space-x-5">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                        Min price
                      </label>
                      <div className="mt-1 relative rounded-md">
                        <input
                          type="text"
                          disabled
                          id="minPrice"
                          className="focus:ring-primary-500 focus:border-primary-500 block w-full pl-7 pr-3 sm:text-sm border-neutral-300 dark:border-neutral-600 rounded-md bg-neutral-50 dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100"
                          value={`$${convertNumbThousand(priceRange[0])}`}
                        />
                      </div>
                    </div>
                    <div>
                      <label
                        htmlFor="maxPrice"
                        className="block text-sm font-medium text-neutral-700 dark:text-neutral-300"
                      >
                        Max price
                      </label>
                      <div className="mt-1 relative rounded-md">
                        <input
                          type="text"
                          disabled
                          name="maxPrice"
                          id="maxPrice"
                          className="focus:ring-primary-500 focus:border-primary-500 block w-full pl-7 pr-3 sm:text-sm border-neutral-300 dark:border-neutral-600 rounded-md bg-neutral-50 dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100"
                          value={`$${convertNumbThousand(priceRange[1])}`}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="p-5 bg-neutral-50 dark:bg-neutral-900 dark:border-t dark:border-neutral-800 flex items-center justify-between">
                  <ButtonThird
                    onClick={() => onClear(close)}
                    sizeClass="px-4 py-2 sm:px-5"
                  >
                    Clear
                  </ButtonThird>
                  <ButtonPrimary
                    onClick={() => onApply(close)}
                    sizeClass="px-4 py-2 sm:px-5"
                  >
                    Apply
                  </ButtonPrimary>
                </div>
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
};

export default PriceRangeFilter;